import type { FieldStats, LayerStyle } from '@/utils/layerStyle';
import type { LayerLook } from './layerStyles.service';
import apiClient from './api';
import { loadAuthConfig } from '@/auth/authConfig';
import { renewNow } from '@/auth/umsSession';

/**
 * Workspaces and the company's own layers in them (Veramenta docs/en/59 sec.3.2, 60 sec.10).
 *
 * A workspace is a node of the company in the user management service: made, renamed, archived and shared there
 * (`/me/scopes`), with the person's own token. The layers in it are GeoNexora's (`/api/v2/workspaces`). Without the
 * user management service (a laptop's `dev` sign-in) there is one workspace, so the layers can still be tried.
 */

export const WORKSPACE_TYPE = 'geonexora.workspace';
export const DEV_WORKSPACE = '00000000-0000-0000-0000-00000000aaaa';

export interface Workspace {
  id: string;
  name: string;
  roles: string[];
  canManage: boolean;
  archived?: boolean;
}

export interface Share {
  principalId: string;
  displayName: string;
  email?: string;
  role: string;
}

export interface Colleague {
  principalId: string;
  displayName: string;
  email?: string;
}

export interface CompanyLayerField {
  key: string;
  column: string;
  type: string;
  labels: Record<string, string>;
  fieldClass: string;
  hidden: boolean;
}

export interface CompanyLayer {
  key: string;
  /** What an analysis is asked for it by: co:<key>. */
  name: string;
  typeKey: string;
  workspace: string;
  labels: Record<string, string>;
  visible: boolean;
  style?: LayerStyle | null;
  /** The field written beside each feature, by key. */
  label?: string | null;
  /** Not drawn when zoomed out further than 1:minScale, or in further than 1:maxScale. */
  minScale?: number | null;
  maxScale?: number | null;
  /** What the features call a field where that is not its key. */
  columns?: Record<string, string>;
  /** Null for the company's data without a place. */
  geometry: 'point' | 'line' | 'area' | 'any' | null;
  /** The company's data without a place (no map, no eye); its key is the type key. */
  dataOnly?: boolean;
  count: number;
  canUpdate: boolean;
  canDelete: boolean;
  fields?: CompanyLayerField[];
  /** The caller's own view of the layer, drawn instead of its main look; null when they keep none. */
  mine?: LayerLook & { updatedAt?: string } | null;
  /** Whether the caller may change the main look (workspace.layer.style:update). */
  canEditMain?: boolean;
  /** The ways this geometry may be drawn, and whether its points may be clustered. */
  modes?: string[];
  clusters?: boolean;
  read?: number;
  created?: number;
  refused?: string[];
}

async function ums(path: string, init: RequestInit = {}): Promise<Response> {
  const config = await loadAuthConfig();
  if (config.mode !== 'ums' || !config.umsUrl) throw new Error('no user management service');
  const attempt = () => fetch(`${config.umsUrl!.replace(/\/$/, '')}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}`,
      ...(init.headers ?? {}),
    },
  });
  let response = await attempt();
  // UMS tokens live minutes: a refused one is renewed once, silently.
  if (response.status === 401 && (await renewNow())) {
    response = await attempt();
  }
  if (!response.ok) {
    let why = `${response.status}`;
    try {
      const body = await response.json();
      why = body.detail || body.message || body.title || why;
    } catch {
      /* not JSON */
    }
    throw new Error(why);
  }
  return response;
}

export async function hasWorkspaces(): Promise<boolean> {
  return (await loadAuthConfig()).mode === 'ums';
}

export const workspaceService = {
  async list(): Promise<Workspace[]> {
    if (!(await hasWorkspaces())) {
      return [{ id: DEV_WORKSPACE, name: 'Development workspace', roles: ['workspace-owner'], canManage: true }];
    }
    return (await ums(`/me/scopes?type=${encodeURIComponent(WORKSPACE_TYPE)}`)).json();
  },
  async create(name: string): Promise<string> {
    const r = await ums('/me/scopes', { method: 'POST', body: JSON.stringify({ type: WORKSPACE_TYPE, name }) });
    return (await r.json()).id;
  },
  async rename(id: string, name: string): Promise<void> {
    await ums(`/me/scopes/${id}`, { method: 'PATCH', body: JSON.stringify({ name, archive: false }) });
  },
  async archive(id: string): Promise<void> {
    await ums(`/me/scopes/${id}`, { method: 'PATCH', body: JSON.stringify({ archive: true }) });
  },
  async shares(id: string): Promise<Share[]> {
    return (await ums(`/me/scopes/${id}/shares`)).json();
  },
  async share(id: string, principal: string, role: string): Promise<void> {
    await ums(`/me/scopes/${id}/shares/${principal}`, { method: 'PUT', body: JSON.stringify({ role }) });
  },
  async unshare(id: string, principal: string): Promise<void> {
    await ums(`/me/scopes/${id}/shares/${principal}`, { method: 'DELETE' });
  },
  async colleagues(): Promise<Colleague[]> {
    return (await ums('/me/colleagues')).json();
  },

  async layers(workspace: string): Promise<CompanyLayer[]> {
    return (await apiClient.get<CompanyLayer[]>(`/v2/workspaces/${workspace}/layers`)).data;
  },
  async allLayers(): Promise<CompanyLayer[]> {
    return (await apiClient.get<CompanyLayer[]>('/v2/workspaces/layers')).data;
  },
  async layer(workspace: string, key: string): Promise<CompanyLayer> {
    return (await apiClient.get<CompanyLayer>(`/v2/workspaces/${workspace}/layers/${key}`)).data;
  },
  async upload(workspace: string, file: File, name: string, csv?: { lon?: string; lat?: string; wkt?: string; separator?: string }):
    Promise<CompanyLayer> {
    const form = new FormData();
    form.append('file', file);
    if (name) form.append('name', name);
    Object.entries(csv ?? {}).forEach(([k, v]) => { if (v) form.append(k, v); });
    return (await apiClient.post<CompanyLayer>(`/v2/workspaces/${workspace}/layers`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 10 * 60 * 1000,
    })).data;
  },
  async change(workspace: string, key: string, change: {
    labels?: Record<string, string>;
    visible?: boolean;
    style?: LayerStyle | Record<string, unknown>;
    label?: string | null;
    minScale?: number | null;
    maxScale?: number | null;
    fields?: { key: string; labels?: Record<string, string>; fieldClass?: string }[];
  }): Promise<CompanyLayer> {
    return (await apiClient.put<CompanyLayer>(`/v2/workspaces/${workspace}/layers/${key}`, change)).data;
  },
  async remove(workspace: string, key: string): Promise<void> {
    await apiClient.delete(`/v2/workspaces/${workspace}/layers/${key}`);
  },
  /** One field's values, for the style editor: the most common with counts, and a number's range and class edges. */
  async fieldStats(workspace: string, key: string, field: string): Promise<FieldStats> {
    return (await apiClient.get<FieldStats>(`/v2/workspaces/${workspace}/layers/${key}/fields/${encodeURIComponent(field)}/stats`,
      { timeout: 120000 })).data;
  },
  featuresUrl(workspace: string, key: string, bbox: number[]): string {
    return `/v2/workspaces/${workspace}/layers/${key}/features?limit=10000&bbox=${bbox.map((n) => n.toFixed(6)).join(',')}`;
  },
};

export default workspaceService;
